import { Injectable } from '@angular/core';
import {
    LoggedInUser,
    OrganisationAuthorityDto,
    OrganisationAuthorityGroup,
    OrganisationAuthorityService,
    SimpleAuthorityTypeDto,
    UserService,
    UserStatus,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface PersonalTableAuthority {
    name: string;
    identifier: string;
    startDate: string;
    expiryDate: string;
    status: OrganisationAuthorityDto.ExpiryStatus;
    authority: OrganisationAuthorityDto;
    authorityType: SimpleAuthorityTypeDto;
}

@Injectable()
export class PersonalAuthorityDataService {
    private authoritiesSource = new BehaviorSubject<PersonalTableAuthority[]>(
        []
    );
    private workingSource = new ReplaySubject<boolean>(1);

    authorities$ = this.authoritiesSource.asObservable();
    working$ = this.workingSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    private personalOrgId: number | null = null;

    constructor(
        private organisationAuthorityService: OrganisationAuthorityService,
        private userService: UserService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.organisationAuthorityService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                if (this.personalOrgId != null) {
                    this.loadAuthorities();
                }
            });

        this.userService.userChange$
            .pipe(
                filter(
                    user => user != null && user.type === UserStatus.LOGGED_IN
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe((user: LoggedInUser) => {
                this.personalOrgId = user.currentUser.personalOrganisationId;
                this.loadAuthorities();
            });
    }

    ngOnDestroy() {
        this.authoritiesSource.complete();
        this.workingSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    loadAuthorities() {
        if (this.personalOrgId == null) {
            return;
        }

        this.organisationAuthorityService
            .findAuthorities(this.personalOrgId, this.personalOrgId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: groups => {
                    this.authoritiesSource.next(this.buildTableData(groups));
                },
                error: () => {
                    this.authoritiesSource.next([]);
                }
            })
            .add(this.workTracker.createTracker());
    }

    private buildTableData(
        groups: OrganisationAuthorityGroup[]
    ): PersonalTableAuthority[] {
        return groups.reduce((acc: PersonalTableAuthority[], group) => {
            const withExpiry = (group.authorities ?? [])
                .filter(
                    a =>
                        a.expiryDate != null &&
                        (a.expiryStatus ===
                            OrganisationAuthorityDto.ExpiryStatus.EXPIRED ||
                            a.expiryStatus ===
                                OrganisationAuthorityDto.ExpiryStatus.UPCOMING)
                )
                .map(a => ({
                    name: group.name,
                    identifier: a.identifier,
                    startDate: a.startDate,
                    expiryDate: a.expiryDate,
                    status: a.expiryStatus,
                    authority: a,
                    authorityType: group as unknown as SimpleAuthorityTypeDto
                }));
            return acc.concat(withExpiry);
        }, []);
    }
}
